"use client";

import { MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PostLocationTag as PostLocationTagData } from "@/db/schema/post";
import { cn } from "@/lib/utils";

type PostLocationTagProps = {
  location: PostLocationTagData;
  compact?: boolean;
  onRemove?: () => void;
  /**
   * Link the pill to Maps (feed cards). The composer passes false so a tap
   * never leaves a draft.
   */
  asLink?: boolean;
};

export function PostLocationTag({
  location,
  compact = false,
  onRemove,
  asLink = true,
}: PostLocationTagProps) {
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;

  // Feed cards use a lightweight pill so tags don't compete with the media
  // and caption; the full row is reserved for composer trays.
  if (compact) {
    const pillBody = (
      <>
        <MapPin className="h-3.5 w-3.5 shrink-0 text-brand" />
        <span className="truncate text-xs font-medium text-text-primary">
          {location.displayName}
        </span>
      </>
    );
    return (
      <div
        className={cn(
          "flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-border bg-surface-alt/60 py-1 pl-2.5",
          onRemove ? "pr-1" : "pr-2.5",
        )}
      >
        {asLink ? (
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="flex min-w-0 items-center gap-1.5"
            aria-label={`Open ${location.displayName} in Maps`}
          >
            {pillBody}
          </a>
        ) : (
          <span className="flex min-w-0 items-center gap-1.5">{pillBody}</span>
        )}
        {onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0 rounded-full text-text-secondary"
            onClick={onRemove}
            aria-label="Remove location"
          >
            <X className="h-3 w-3" />
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 rounded-xl border border-border bg-surface-alt px-3.5 py-3 text-left">
      <a
        href={mapsHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand"
        aria-label={`Open ${location.displayName} in Maps`}
      >
        <MapPin className="h-4 w-4" />
      </a>
      <a
        href={mapsHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        className="min-w-0 flex-1"
      >
        <p className="truncate text-sm font-semibold text-text-primary">
          {location.displayName}
        </p>
        {location.address ? (
          <p className="mt-0.5 line-clamp-1 text-xs text-text-secondary">
            {location.address}
          </p>
        ) : null}
      </a>
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-full text-text-secondary"
          onClick={onRemove}
          aria-label="Remove location"
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
